if (!customElements.get('oscar-hero-slider')) {
  customElements.define('oscar-hero-slider', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      if (this.slides.length < 2) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.tabs = Array.from(this.querySelectorAll('[data-slide-index]'));
      this.stage = this.querySelector('.os-slider-stage');
      this.toggle = this.querySelector('[data-toggle]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.index = 0;
      this.hovered = false;
      this.visible = false;
      this.paused = this.dataset.autoplay !== 'true' || this.motion.matches || Boolean(window.Shopify?.designMode);
      this.interval = Math.max(5000, Number(this.dataset.interval) || 7000);
      this.rtl = getComputedStyle(this).direction === 'rtl';
      this.querySelector('[data-controls]').hidden = false;
      this.toggle.addEventListener('pointerdown', () => { this.toggleNextPaused = !this.paused; }, options);
      this.addEventListener('click', event => {
        if (Date.now() < this.suppressClickUntil) {
          event.preventDefault();
          this.suppressClickUntil = 0;
          return;
        }
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.matches('[data-toggle]')) {
          this.paused = event.detail > 0 && typeof this.toggleNextPaused === 'boolean' ? this.toggleNextPaused : !this.paused;
          this.toggleNextPaused = undefined;
          this.updatePlayback();
        } else if (button.matches('[data-step]')) {
          this.show(this.index + Number(button.dataset.step), true);
        } else if (button.matches('[data-slide-index]')) {
          this.show(Number(button.dataset.slideIndex), true);
        }
      }, options);
      this.addEventListener('keydown', event => {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        const steps = { ArrowRight: this.rtl ? -1 : 1, ArrowLeft: this.rtl ? 1 : -1 };
        if (event.key in steps) {
          event.preventDefault();
          this.show(this.index + steps[event.key], true);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.show(event.key === 'Home' ? 0 : this.slides.length - 1, true);
        }
      }, options);
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = true;
        this.schedule();
      }, options);
      this.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        this.hovered = false;
        this.schedule();
      }, options);
      this.addEventListener('focusin', event => {
        if (!this.contains(event.relatedTarget)) {
          this.paused = true;
          this.updatePlayback();
        }
      }, options);
      this.stage.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'touch') return;
        this.touch = { x: event.clientX, y: event.clientY };
        this.suppressClickUntil = 0;
        this.paused = true;
        this.updatePlayback();
      }, options);
      this.stage.addEventListener('pointerup', event => {
        if (!this.touch) return;
        const dx = event.clientX - this.touch.x;
        const dy = event.clientY - this.touch.y;
        this.touch = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.suppressClickUntil = Date.now() + 400;
          const step = dx < 0 ? 1 : -1;
          this.show(this.index + (this.rtl ? -step : step), true);
        }
      }, options);
      this.stage.addEventListener('pointercancel', () => { this.touch = null; }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', event => {
        if (event.matches) this.paused = true;
        this.updatePlayback();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const index = this.slides.findIndex(slide => slide.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.show(index, true);
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.warmNext();
        this.schedule();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.updatePlayback();
    }
    show(index, manual = false) {
      if (manual) this.paused = true;
      const next = (index + this.slides.length) % this.slides.length;
      const focused = this.slides[this.index].contains(document.activeElement);
      this.index = next;
      this.slides.forEach((slide, i) => {
        const active = i === next;
        slide.hidden = !active;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        this.tabs[i].setAttribute('aria-pressed', String(active));
      });
      this.querySelector('[data-count]').textContent = String(next + 1).padStart(2, '0');
      if (focused) this.tabs[next].focus({ preventScroll: true });
      const image = this.slides[next].querySelector('img');
      if (image) image.loading = 'eager';
      this.warmNext();
      this.updatePlayback();
    }
    warmNext() {
      const image = this.slides[(this.index + 1) % this.slides.length].querySelector('img');
      if (image) image.loading = 'eager';
    }
    updatePlayback() {
      this.toggle.setAttribute('aria-label', this.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.toggle.querySelector('[data-pause-icon]').toggleAttribute('hidden', this.paused);
      this.toggle.querySelector('[data-play-icon]').toggleAttribute('hidden', !this.paused);
      this.stage.setAttribute('aria-live', this.paused ? 'polite' : 'off');
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (this.paused || this.hovered || !this.visible || document.hidden) return;
      this.timer = setTimeout(() => this.show(this.index + 1), this.interval);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.observer?.disconnect();
      this.controller = null;
    }
  });
}
